import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, RotateCw, Save } from "lucide-react";
import { showErrorAlert, showSuccessAlert } from "../../../../../components/HelperAlerts/HelpersAlerts.jsx";

const formatApiError = (err, fallback) => {
    const data = err.response?.data;
    if (!data) return fallback;
    if (typeof data === "string") return data;
    if (data.detail) return data.detail;
    return Object.entries(data)
        .map(([field, value]) => `${field}: ${Array.isArray(value) ? value.join(", ") : value}`)
        .join(" | ");
};

export default function ConfiguracionSection() {
    const [settings, setSettings] = useState(null);
    const [state, setState] = useState("loading");
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        setState("loading");
        axios.get("/fulfillment-admin/production-settings/")
            .then(({ data }) => { setSettings(data); setState("ready"); })
            .catch((err) => {
                console.error("Error loading production settings:", err);
                setState(err.response?.status === 403 ? "forbidden" : "error");
            });
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleSave = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            const { data } = await axios.patch("/fulfillment-admin/production-settings/", {
                safety_percent: settings.safety_percent,
                minimum_setup_seconds: settings.minimum_setup_seconds,
                max_quantity_per_request: settings.max_quantity_per_request,
            });
            setSettings(data);
            showSuccessAlert({ title: "Configuración guardada", text: "Solo afecta a nuevas estimaciones." });
        } catch (err) {
            console.error("Error saving production settings:", err);
            showErrorAlert({ title: "No se pudo guardar", text: formatApiError(err, "Revisa los valores.") });
        } finally {
            setSaving(false);
        }
    };

    if (state === "loading") {
        return <div className="flex justify-center py-10"><span className="loading loading-spinner loading-lg text-primary" /></div>;
    }
    if (state === "forbidden") {
        return (
            <div role="alert" className="alert alert-warning text-sm">
                <AlertTriangle className="w-4 h-4" />
                Tu cuenta no tiene el permiso <code>fulfillment.manage_production_settings</code>.
            </div>
        );
    }
    if (state === "error") {
        return (
            <div role="alert" className="alert alert-error text-sm">
                <AlertTriangle className="w-4 h-4" />
                No se pudo cargar la configuración.
                <button type="button" className="btn btn-xs gap-1" onClick={load}><RotateCw className="w-3 h-3" />Reintentar</button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSave} className="glassCard rounded-xl p-4 flex flex-col gap-3 max-w-lg">
            <p className="text-sm text-base-content/60">
                Cambiar estos valores solo afecta a estimaciones nuevas: cada reserva conserva el margen
                aplicado en su propio snapshot.
            </p>
            <label className="form-control gap-1">
                <span className="label-text text-xs">Porcentaje de seguridad (%)</span>
                <input
                    type="number" step="0.01" min="0" className="input input-bordered input-sm"
                    value={settings.safety_percent}
                    onChange={(e) => setSettings((s) => ({ ...s, safety_percent: e.target.value }))}
                />
            </label>
            <label className="form-control gap-1">
                <span className="label-text text-xs">Preparación mínima (segundos)</span>
                <input
                    type="number" min="0" className="input input-bordered input-sm"
                    value={settings.minimum_setup_seconds}
                    onChange={(e) => setSettings((s) => ({ ...s, minimum_setup_seconds: e.target.value }))}
                />
            </label>
            <label className="form-control gap-1">
                <span className="label-text text-xs">Límite técnico de cantidad por solicitud</span>
                <input
                    type="number" min="1" className="input input-bordered input-sm"
                    value={settings.max_quantity_per_request}
                    onChange={(e) => setSettings((s) => ({ ...s, max_quantity_per_request: e.target.value }))}
                />
                <span className="text-xs text-base-content/50">
                    Límite técnico anti-abuso, no una capacidad comercial garantizada.
                </span>
            </label>
            <div className="flex justify-end">
                <button type="submit" className="btn btn-sm btn-primary gap-2" disabled={saving}>
                    {saving ? <span className="loading loading-spinner loading-xs" /> : <Save className="w-4 h-4" />}
                    Guardar
                </button>
            </div>
        </form>
    );
}
